import { type ReactNode, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { formatTextDate } from '@witmetrics/utils/time';
import { useDateFormatPreference } from '@witmetrics/data-store';
import { useToggle } from '@/hooks/useToggle';
import ContentMenu from '@/menus/ContentMenu';
import { OptionsIcon } from '@/icons';
import Icon from '@/components/Icon';
import Card from '@/components/Card';
import Chip from '@/components/Chip';
import TruncatedText from '@/components/TruncatedText';
import type { BaseItemProps } from '../../types';
import { classes, styles } from './utils';

export interface GridItemProps extends BaseItemProps {
  compact?: boolean;
  preview?: ReactNode;
}

export default function GridItem({
  compact,
  overrideIcon,
  variant,
  className,
  name,
  tag,
  details,
  preview,
  updatedAt,
  iconVariant,
  icon,
  onDownload,
  onShare,
  onMove,
  onView,
  onEdit,
  onDelete,
}: GridItemProps) {
  const menuToggleRef = useRef(null);
  const dateFormat = useDateFormatPreference();
  const [isMenuOpen, toggleMenu] = useToggle();
  const hasPreview = !!preview;
  const isAurora = variant === 'aurora';
  const showOverlay = isAurora || hasPreview;
  const kebabOnDark = hasPreview || (isAurora && !compact);

  const handleEdit = () => {
    toggleMenu(false);
    onEdit();
  };

  const handleDelete = () => {
    toggleMenu(false);
    onDelete();
  };

  const renderPreview = () => {
    if (preview) return preview;
    if (overrideIcon) return icon;
    return <Icon variant={iconVariant} size="base" icon={icon} />;
  };

  const optionsMenu = (
    <div className={classes.optionsWrapper}>
      <OptionsIcon
        ref={menuToggleRef}
        className={twMerge(
          classes.optionsIcon,
          kebabOnDark && classes.optionsIconPreview
        )}
        color={kebabOnDark ? 'white' : 'grey-6'}
        onClick={() => toggleMenu()}
      />
      <ContentMenu
        anchorTo={menuToggleRef.current}
        open={isMenuOpen}
        onDownload={onDownload}
        onShare={onShare}
        onMove={onMove}
        onView={onView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onClose={() => toggleMenu()}
      />
    </div>
  );

  if (compact) {
    return (
      <Card
        sx={styles.card}
        className={twMerge(
          isAurora ? classes.auroraCompactCard : classes.compactCard,
          isMenuOpen && classes.menuOpen,
          className
        )}>
        {overrideIcon ? icon : <Icon variant={iconVariant} icon={icon} />}
        <div className={classes.compactBody} onClick={onView}>
          <TruncatedText
            className={twMerge(
              classes.compactName,
              isAurora && classes.auroraCompactName
            )}
            maxLength={18}
            onClick={onView}>
            {name}
          </TruncatedText>
          {tag && (
            <div
              className={
                isAurora ? classes.auroraCompactCount : classes.compactCount
              }>
              {tag}
            </div>
          )}
        </div>
        <div className={classes.compactOptions}>{optionsMenu}</div>
      </Card>
    );
  }

  return (
    <Card
      sx={styles.card}
      className={twMerge(
        isAurora ? classes.auroraCard : classes.card,
        isMenuOpen && classes.menuOpen,
        className
      )}>
      <div className={classes.previewWrapper} onClick={onView}>
        {renderPreview()}
      </div>
      {showOverlay && (
        <div
          className={
            isAurora ? classes.auroraPreviewOverlay : classes.previewOverlay
          }
        />
      )}
      <div
        className={twMerge(
          classes.topScrim,
          showOverlay && classes.topScrimPreview
        )}>
        <div className={classes.title}>
          <TruncatedText
            className={twMerge(
              classes.name,
              showOverlay && classes.namePreview
            )}
            maxLength={isAurora ? 18 : 22}
            onClick={onView}>
            {name}
          </TruncatedText>
          {tag && (
            <Chip
              className={twMerge(
                classes.pill,
                isAurora && classes.auroraPill,
                showOverlay && classes.overlayPill
              )}>
              {tag}
            </Chip>
          )}
        </div>
        {optionsMenu}
      </div>
      {(details || updatedAt) && (
        <div
          className={twMerge(
            classes.bottomScrim,
            showOverlay && classes.bottomScrimPreview
          )}>
          {details && <div>{details}</div>}
          {updatedAt && <div>{formatTextDate(updatedAt, dateFormat)}</div>}
        </div>
      )}
    </Card>
  );
}
